import type { Column, ColumnDef, ExpandedState, OnChangeFn, TableOptions } from '@tanstack/react-table';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';

import { fuzzyFilter } from '../../../../helpers/filters/fuzzyFilter';
import { TABLE_CONSTANTS } from '../../shared/constants';
import type { TableState } from '../../shared/hooks/useTableState';

import type { UseTableStateHandlersReturn } from './useTableStateHandlers';

export interface UseTableConfigOptions<TData = any> {
  columns: ColumnDef<TData>[];
  data: TData[];
  enableColumnFilters: boolean;
  tableState: TableState | undefined;
  initialState: TableState;
  expanded: ExpandedState;
  onExpandedChange: OnChangeFn<ExpandedState>;
  stateHandlers: UseTableStateHandlersReturn;
}

export type UseTableConfigReturn<TData = any> = TableOptions<TData>;

export function useTableConfig<TData = any>({
  columns,
  data,
  enableColumnFilters,
  tableState,
  initialState,
  expanded,
  onExpandedChange,
  stateHandlers,
}: UseTableConfigOptions<TData>): UseTableConfigReturn<TData> {
  return {
    autoResetPageIndex: false,
    columns,
    data,
    enableColumnPinning: true,
    initialState: {
      columnVisibility: initialState.columnVisibility,
      columnPinning: initialState.columnPinning,
    },
    state: {
      columnFilters: tableState?.columnFilters ?? initialState.columnFilters,
      columnOrder: tableState?.columnOrder ?? initialState.columnOrder,
      columnPinning: initialState.columnPinning,
      columnVisibility: tableState?.columnVisibility ?? initialState.columnVisibility,
      expanded,
      globalFilter: tableState?.globalFilter ?? initialState.globalFilter,
      pagination: tableState?.pagination ?? initialState.pagination,
      sorting: tableState?.sorting ?? initialState.sorting,
    },
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    enableColumnFilters,
    enableExpanding: tableState?.isGroupByParent ?? false,
    paginateExpandedRows: false,
    sortDescFirst: true,
    enableMultiSort: false,
    enableSortingRemoval: true,
    globalFilterFn: fuzzyFilter,
    getColumnCanGlobalFilter: (column: Column<TData>) => !TABLE_CONSTANTS.EXCLUDED_COLUMNS.includes(column.id as any),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getSubRows: (row: TData) => (row as any).subRows,
    onExpandedChange,
    filterFromLeafRows: true,
    maxLeafRowFilterDepth: 1,
    ...stateHandlers,
  };
}
